/**
 * QuickBooks' side of the "Review matching clients" step (T4-144, spec 015): the provider config for the shared
 * integration_match_review components, the mapping from the Lambda's preview groups to their neutral shape and
 * back, and what a reviewed import sends. Pure.
 */

const id = suffix => `integrations.quickbooks.match_review.${suffix}`;

export const QUICKBOOKS_MATCH_REVIEW = {
	messages: {
		description: id('description'),
		autoLinked: id('auto_linked'),
		autoLinkedOnly: id('auto_linked_only'),
		confirmedCount: id('confirmed_count'),
		resolved: id('resolved'),
		needsReview: id('needs_review'),
		manyClientsTitle: id('many_clients.title'),
		manyClientsPrompt: id('many_clients.prompt'),
		manySourcesTitle: id('many_customers.title'),
		manySourcesPrompt: id('many_customers.prompt'),
		manyToManyTitle: id('many_to_many.title'),
		manyToManyPrompt: id('many_to_many.prompt'),
		doNotLink: id('do_not_link'),
		doNotLinkSource: id('do_not_link_customer'),
		takenBy: id('taken_by'),
	},
	testIdPrefix: 'quickbooks-match',
};

const KIND = {MANY_CLIENTS: 'MANY_CLIENTS', MANY_CUSTOMERS: 'MANY_SOURCES', MANY_TO_MANY: 'MANY_TO_MANY'};

/** A customer's identity across connected companies: its id repeats between them, so the realm is part of it. */
export const sourceIdOf = customer => `${customer.realmId}:${customer.id}`;

/**
 * The Lambda's groups in the neutral shape. The group's companies follow its title, as the design draws; each
 * customer's company is on its option only when the group spans more than one. Missing names take the fallbacks.
 * A group spans companies by realm, not by name: two unnamed companies share the fallback name.
 */
export const toNeutralGroups = (groups, fallbacks) =>
	groups.map(group => {
		const companyOf = customer => customer.companyName || fallbacks.companyName(customer.realmId);
		const companies = [...new Set(group.customers.map(companyOf))];
		const spans = new Set(group.customers.map(customer => customer.realmId)).size > 1;

		return {
			key: group.key,
			email: group.email,
			kind: KIND[group.kind],
			context: companies.join(', '),
			sources: group.customers.map(customer => ({
				id: sourceIdOf(customer),
				name: customer.name,
				detail: spans ? companyOf(customer) : undefined,
			})),
			clients: group.clients.map(client => ({
				clientId: client.clientId,
				name: client.name || fallbacks.clientName(client.clientId),
			})),
		};
	});

/** sourceId -> {id, realmId}, so pairings map back without ever splitting a sourceId. */
export const customerIndex = groups => {
	const index = new Map();
	for (const group of groups) {
		for (const customer of group.customers) {
			index.set(sourceIdOf(customer), {id: customer.id, realmId: customer.realmId});
		}
	}
	return index;
};

/** The neutral pairings as the mutation's resolutions. A pairing whose source is unknown is dropped. */
export const toQuickbooksResolutions = (pairings, index) =>
	pairings.flatMap(pairing => {
		const customer = index.get(pairing.sourceId);
		return customer ? [{customerId: customer.id, realmId: customer.realmId, clientId: pairing.clientId}] : [];
	});

/**
 * What a reviewed import sends (spec 015, "The modal sends only what is to be imported"): every selected customer
 * outside a group, and from each group only the customers the Admin picked. An unpicked customer is not in the
 * request, so the import cannot create or link it.
 */
export const customersToImport = (selected, groups, resolutions) => {
	const grouped = new Set(groups.flatMap(group => group.customers.map(sourceIdOf)));
	const picked = new Set(resolutions.map(resolution => sourceIdOf({id: resolution.customerId, realmId: resolution.realmId})));
	return selected.filter(customer => !grouped.has(sourceIdOf(customer)) || picked.has(sourceIdOf(customer)));
};
